import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import {
  Home,
  LogOut,
  FileText,
  Users,
  DollarSign,
  TrendingUp,
  Clock,
  Settings,
  Menu,
  X,
  Briefcase,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  Calendar,
  Building2,
  ChevronRight,
  Phone,
  Mail,
  User,
  Search,
  Filter
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Realtor Sidebar (shared component)
function RealtorSidebar({ realtor, onLogout }) {
  const [collapsed, setCollapsed] = useState(false);
  
  const navItems = [
    { icon: Home, label: "Dashboard", path: "/realtor/dashboard" },
    { icon: FileText, label: "Listings", path: "/realtor/listings" },
    { icon: Briefcase, label: "Transactions", path: "/realtor/transactions" },
    { icon: Users, label: "Buyers", path: "/realtor/buyers" },
    { icon: Building2, label: "Sellers", path: "/realtor/sellers" },
    { icon: DollarSign, label: "Commission", path: "/realtor/commission" },
    { icon: TrendingUp, label: "Analytics", path: "/realtor/analytics" },
    { icon: Settings, label: "Settings", path: "/realtor/settings" },
  ];

  return (
    <aside className={`fixed left-0 top-0 h-full bg-black border-r border-white/[0.06] transition-all z-40 ${
      collapsed ? "w-16" : "w-64"
    }`}>
      <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
        {!collapsed && (
          <Link to="/realtor/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gold/10 border border-gold/30 flex items-center justify-center">
              <Home className="w-4 h-4 text-gold" />
            </div>
            <span className="font-heading font-bold text-white">REALTOR</span>
          </Link>
        )}
        <button onClick={() => setCollapsed(!collapsed)} className="text-white/50 hover:text-white p-1">
          {collapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
        </button>
      </div>

      <nav className="p-2 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = window.location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 transition-all duration-300 ${
                isActive ? "bg-gold/10 text-gold border-l-2 border-gold" : "text-white/50 hover:text-white hover:bg-[#0A0A0A]/80 backdrop-blur-sm"
              }`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span className="text-sm">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {!collapsed && (
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/[0.06]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#0A0A0A]/80 backdrop-blur-sm rounded-full flex items-center justify-center">
              <span className="text-gold font-bold">{realtor?.name?.charAt(0) || "R"}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{realtor?.name}</p>
              <p className="text-white/40 text-xs truncate">{realtor?.brokerage || "Agent"}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 text-white/50 hover:text-[#FF3B30] py-2 border border-white/[0.08] hover:border-red-500/50 transition-all duration-300"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      )}
    </aside>
  );
}

// Transaction Stage Definitions
const TRANSACTION_STAGES = [
  { key: "under_contract", label: "Under Contract", color: "blue" },
  { key: "inspection", label: "Inspection", color: "purple" },
  { key: "appraisal", label: "Appraisal", color: "amber" },
  { key: "financing", label: "Financing", color: "cyan" },
  { key: "title_clear", label: "Title Clear", color: "green" },
  { key: "final_walkthrough", label: "Final Walkthrough", color: "emerald" },
  { key: "closing", label: "Closing", color: "teal" },
  { key: "closed", label: "Closed", color: "zinc" }
];

const STAGE_COLORS = {
  under_contract: "bg-blue-500/20 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] border-blue-500/30",
  inspection: "bg-purple-500/20 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)] border-purple-500/30",
  appraisal: "bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] border-amber-500/30",
  financing: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
  title_clear: "bg-green-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border-green-500/30",
  final_walkthrough: "bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border-emerald-500/30",
  closing: "bg-teal-500/20 text-teal-400 border-teal-500/30",
  closed: "bg-white/20/20 text-white/50 border-white/[0.15]/30"
};

function StageBadge({ stage }) {
  const stageInfo = TRANSACTION_STAGES.find(s => s.key === stage);
  return (
    <span className={`px-2 py-1 text-xs font-medium border ${STAGE_COLORS[stage] || "bg-white/[0.08] text-white/70 border-white/[0.1]"}`}>
      {stageInfo?.label || stage}
    </span>
  );
}

// Stage Progress Bar
function StageProgressBar({ currentStage }) {
  const currentIndex = TRANSACTION_STAGES.findIndex(s => s.key === currentStage);

  return (
    <div className="flex items-center justify-between overflow-x-auto pb-2">
      {TRANSACTION_STAGES.map((stage, index) => {
        const isComplete = index < currentIndex;
        const isCurrent = index === currentIndex;

        return (
          <div key={stage.key} className="flex items-center">
            <div className="flex flex-col items-center min-w-[70px]">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center mb-1 ${
                isComplete ? "bg-gold text-black" :
                isCurrent ? "bg-gold/20 border-2 border-gold text-gold" :
                "bg-white/[0.08] text-white/40"
              }`}>
                {isComplete ? <CheckCircle className="w-3 h-3" /> : <span className="text-xs">{index + 1}</span>}
              </div>
              <span className={`text-[9px] text-center whitespace-nowrap ${
                isCurrent ? "text-gold font-medium" : isComplete ? "text-white/70" : "text-white/40"
              }`}>
                {stage.label}
              </span>
            </div>
            {index < TRANSACTION_STAGES.length - 1 && (
              <div className={`w-6 h-0.5 mx-0.5 ${isComplete ? "bg-gold" : "bg-white/[0.08]"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// Update Stage Modal
function UpdateStageModal({ isOpen, onClose, transaction, onUpdate }) {
  const [selectedStage, setSelectedStage] = useState(transaction?.stage || "");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (transaction) {
      setSelectedStage(transaction.stage);
    }
  }, [transaction?.stage]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onUpdate(selectedStage, notes);
      onClose();
    } catch (err) {
      toast.error("Failed to update stage");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] w-full max-w-md">
        <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-white">Update Transaction Stage</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="text-white/50 text-sm block mb-2">New Stage</label>
            <select
              value={selectedStage}
              onChange={(e) => setSelectedStage(e.target.value)}
              className="w-full bg-black border border-white/[0.08] text-white px-3 py-2"
            >
              {TRANSACTION_STAGES.map((stage) => (
                <option key={stage.key} value={stage.key}>{stage.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-white/50 text-sm block mb-2">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-black border border-white/[0.08] text-white px-3 py-2 min-h-[80px]"
              placeholder="Add notes about this update..."
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose} className="border-white/[0.1] text-white/70 rounded-none">
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={loading} className="bg-gold hover:bg-gold/90 text-black rounded-none">
              {loading ? "Updating..." : "Update Stage"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RealtorTransactionsPage() {
  const navigate = useNavigate();
  const [realtor, setRealtor] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [stageFilter, setStageFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, txnRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/realtor/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/realtor/transactions`, { withCredentials: true })
      ]);
      setRealtor(profileRes.data);
      setTransactions(txnRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/realtor");
      } else {
        toast.error("Failed to load transactions");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/realtor/auth/logout`, {}, { withCredentials: true });
      navigate("/realtor");
    } catch (error) {
      navigate("/realtor");
    }
  };

  const handleUpdateStage = async (newStage, notes) => {
    if (!selectedTransaction) return;

    try {
      await axios.put(
        `${BACKEND_URL}/api/realtor/transactions/${selectedTransaction.transaction_id}`,
        { stage: newStage, notes },
        { withCredentials: true }
      );
      toast.success("Transaction updated");
      fetchData();
      setShowUpdateModal(false);
    } catch (error) {
      toast.error("Failed to update transaction");
    }
  };

  const filteredTransactions = transactions.filter(txn => {
    const matchesStage = stageFilter === "all" || txn.stage === stageFilter;
    const matchesSearch = !searchTerm || 
      txn.property_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      txn.buyer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStage && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="realtor-transactions-page">
      <RealtorSidebar realtor={realtor} onLogout={handleLogout} />

      <main className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white uppercase">Transactions</h1>
            <p className="text-white/50 mt-1">{transactions.length} total transactions</p>
          </div>
        </div>

        {/* Pipeline Overview */}
        <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 mb-6">
          <h2 className="text-white font-medium mb-4">Pipeline Overview</h2>
          <div className="grid grid-cols-4 lg:grid-cols-8 gap-2">
            {TRANSACTION_STAGES.map((stage) => {
              const count = transactions.filter(t => t.stage === stage.key).length;
              return (
                <button
                  key={stage.key}
                  onClick={() => setStageFilter(stage.key === stageFilter ? "all" : stage.key)}
                  className={`p-3 border text-center transition-all duration-300 ${
                    stageFilter === stage.key 
                      ? "bg-gold/20 border-gold" 
                      : "bg-black border-white/[0.08] hover:border-white/[0.15]"
                  }`}
                >
                  <p className="text-xl font-bold text-white">{count}</p>
                  <p className="text-white/50 text-xs truncate">{stage.label}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 mb-6 flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
              <Input
                placeholder="Search by property or buyer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-black border-white/[0.08] text-white pl-10"
              />
            </div>
          </div>
          <select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            className="bg-black border border-white/[0.08] text-white px-3 py-2"
          >
            <option value="all">All Stages</option>
            {TRANSACTION_STAGES.map((stage) => (
              <option key={stage.key} value={stage.key}>{stage.label}</option>
            ))}
          </select>
        </div>

        {/* Transactions List */}
        <div className="space-y-4">
          {filteredTransactions.length > 0 ? (
            filteredTransactions.map((txn) => (
              <div key={txn.transaction_id} className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-white font-bold text-lg">{txn.property_address}</h3>
                    <p className="text-white/50 text-sm">{txn.buyer_name} • {txn.financing_type}</p>
                  </div>
                  <div className="text-right">
                    <StageBadge stage={txn.stage} />
                    <p className="text-gold font-mono font-bold mt-2">{formatCurrency(txn.sale_price)}</p>
                  </div>
                </div>

                {/* Mini Progress Bar */}
                <div className="mb-4 bg-black p-3 border border-white/[0.08]">
                  <StageProgressBar currentStage={txn.stage} />
                </div>

                {/* Key Info Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-white/40 text-xs">Expected Close</p>
                    <p className="text-white text-sm">
                      {txn.expected_close_date ? new Date(txn.expected_close_date).toLocaleDateString() : "TBD"}
                    </p>
                  </div>
                  <div>
                    <p className="text-white/40 text-xs">Earnest Money</p>
                    <p className="text-white text-sm">{formatCurrency(txn.earnest_money)}</p>
                  </div>
                  <div>
                    <p className="text-white/40 text-xs">Commission</p>
                    <p className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] text-sm font-medium">{formatCurrency(txn.estimated_commission)}</p>
                  </div>
                  <div>
                    <p className="text-white/40 text-xs">Title Company</p>
                    <p className="text-white text-sm">{txn.assigned_title || "Not assigned"}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      setSelectedTransaction(txn);
                      setShowUpdateModal(true);
                    }}
                    className="bg-gold hover:bg-gold/90 text-black rounded-none"
                  >
                    Update Stage
                  </Button>
                  <Button
                    variant="outline"
                    className="border-white/[0.1] text-white/70 hover:bg-white/[0.06] rounded-none"
                    onClick={() => {
                      setSelectedTransaction(txn);
                      setShowDetailModal(true);
                    }}
                  >
                    View Details <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-12 text-center">
              <Briefcase className="w-12 h-12 text-white/30 mx-auto mb-3" />
              <p className="text-white/50">
                {searchTerm || stageFilter !== "all" 
                  ? "No transactions match your filters" 
                  : "No transactions yet"}
              </p>
              {!searchTerm && stageFilter === "all" && (
                <p className="text-white/40 text-sm mt-2">
                  Accept an offer on a listing to create a transaction
                </p>
              )}
            </div>
          )}
        </div>
      </main>

      <UpdateStageModal
        isOpen={showUpdateModal}
        onClose={() => {
          setShowUpdateModal(false);
          setSelectedTransaction(null);
        }}
        transaction={selectedTransaction}
        onUpdate={handleUpdateStage}
      />

      {/* Transaction Detail Modal */}
      {showDetailModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] w-full max-w-2xl my-8">
            <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-white">Transaction Details</h2>
              <button onClick={() => {setShowDetailModal(false); setSelectedTransaction(null);}} className="text-white/50 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {/* Property & Price */}
              <div className="mb-6">
                <h3 className="text-white font-bold text-xl mb-1">{selectedTransaction.property_address}</h3>
                <p className="text-gold font-mono text-2xl font-bold">{formatCurrency(selectedTransaction.sale_price)}</p>
              </div>

              {/* Stage Progress */}
              <div className="bg-black border border-white/[0.08] p-4 mb-6">
                <h4 className="text-white/50 text-sm mb-3">Transaction Progress</h4>
                <StageProgressBar currentStage={selectedTransaction.stage} />
              </div>

              {/* Key Details Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-black border border-white/[0.08] p-3">
                  <p className="text-white/40 text-xs">Buyer</p>
                  <p className="text-white font-medium">{selectedTransaction.buyer_name}</p>
                </div>
                <div className="bg-black border border-white/[0.08] p-3">
                  <p className="text-white/40 text-xs">Financing</p>
                  <p className="text-white font-medium capitalize">{selectedTransaction.financing_type?.replace(/_/g, ' ')}</p>
                </div>
                <div className="bg-black border border-white/[0.08] p-3">
                  <p className="text-white/40 text-xs">Expected Close</p>
                  <p className="text-white font-medium">
                    {selectedTransaction.expected_close_date ? new Date(selectedTransaction.expected_close_date).toLocaleDateString() : "TBD"}
                  </p>
                </div>
                <div className="bg-black border border-white/[0.08] p-3">
                  <p className="text-white/40 text-xs">Earnest Money</p>
                  <p className="text-white font-medium">{formatCurrency(selectedTransaction.earnest_money)}</p>
                </div>
                <div className="bg-black border border-white/[0.08] p-3">
                  <p className="text-white/40 text-xs">Commission Rate</p>
                  <p className="text-white font-medium">{selectedTransaction.commission_rate || 3}%</p>
                </div>
                <div className="bg-black border border-white/[0.08] p-3">
                  <p className="text-white/40 text-xs">Estimated Commission</p>
                  <p className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] font-bold">{formatCurrency(selectedTransaction.estimated_commission)}</p>
                </div>
              </div>

              {/* Assigned Parties */}
              <div className="mb-6">
                <h4 className="text-white/50 text-sm mb-3">Assigned Parties</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-black border border-white/[0.08] p-3">
                    <p className="text-white/40 text-xs">Lender</p>
                    <p className="text-white">{selectedTransaction.assigned_lender || "Not assigned"}</p>
                  </div>
                  <div className="bg-black border border-white/[0.08] p-3">
                    <p className="text-white/40 text-xs">Title Company</p>
                    <p className="text-white">{selectedTransaction.assigned_title || "Not assigned"}</p>
                  </div>
                </div>
              </div>

              {/* Stage History */}
              {selectedTransaction.stage_history?.length > 0 && (
                <div>
                  <h4 className="text-white/50 text-sm mb-3">Stage History</h4>
                  <div className="space-y-2">
                    {selectedTransaction.stage_history.map((entry, idx) => (
                      <div key={idx} className="flex items-center gap-3 bg-black border border-white/[0.08] p-3">
                        <CheckCircle className="w-4 h-4 text-gold flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-white text-sm capitalize">{entry.stage?.replace(/_/g, ' ')}</p>
                          {entry.notes && <p className="text-white/50 text-xs">{entry.notes}</p>}
                        </div>
                        <p className="text-white/40 text-xs">
                          {new Date(entry.date).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 mt-6 pt-6 border-t border-white/[0.08]">
                <Button
                  onClick={() => {
                    setShowDetailModal(false);
                    setShowUpdateModal(true);
                  }}
                  className="bg-gold hover:bg-gold/90 text-black rounded-none flex-1"
                >
                  Update Stage
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {setShowDetailModal(false); setSelectedTransaction(null);}}
                  className="border-white/[0.1] text-white/70 rounded-none"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
